import { Head, Link } from "@inertiajs/react";
import AdminLayout from "@/components/admin/AdminLayout";
import AccountBadge, { AwaitingPayment } from "@/components/admin/AccountBadge";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "all", label: "All" },
  { key: "awaiting", label: "Awaiting payment" },
  { key: "trial", label: "Free trial" },
  { key: "paid", label: "Paid" },
  { key: "free", label: "Free schools" },
];

export default function Index({ schools, filter, counts, pending_signups_count: pendingSignups }) {
  return (
    <AdminLayout eyebrow="Accounts" title="Schools">
      <Head title="Admin · Schools" />

      <p className="text-sm text-muted-foreground -mt-6 mb-6">
        Schools sign up and start a free trial on their own. When a campaign's fee arrives, open the school and approve the campaign by recording its plan. Until then it can't go live, take donations, add teachers, or send email.
      </p>

      {pendingSignups > 0 && (
        <Notice tone="warning" className="mb-6">
          {pendingSignups} signup{pendingSignups === 1 ? "" : "s"} from before signup was instant {pendingSignups === 1 ? "is" : "are"} still waiting for an account.{" "}
          <Link href="/admin/school_applications?status=pending" className="font-medium text-primary hover:underline">Review</Link>
        </Notice>
      )}

      <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4">
        {TABS.map((tab) => (
          <Link
            key={tab.key}
            href={tab.key === "all" ? "/admin/schools" : `/admin/schools?account=${tab.key}`}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
              filter === tab.key ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary"
            )}
          >
            {tab.label}
            <span className={cn(
              "inline-flex items-center justify-center min-w-[1.5rem] h-5 rounded-full text-[11px] font-mono tabular-nums",
              filter === tab.key ? "bg-primary-foreground/20 text-primary-foreground" : "bg-background text-muted-foreground"
            )}>
              {counts[tab.key]}
            </span>
          </Link>
        ))}
      </div>

      {schools.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-muted-foreground">No schools in this view.</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-3 font-medium">School</th>
                <th className="text-left px-4 py-3 font-medium">Account</th>
                <th className="text-left px-4 py-3 font-medium">Awaiting payment</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-right px-4 py-3 font-medium">Campaigns</th>
                <th className="text-right px-4 py-3 font-medium">Signed up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {schools.map((school) => (
                <tr key={school.id} className="hover:bg-secondary/40 transition-colors align-top">
                  <td className="px-4 py-3">
                    <Link href={school.path} className="font-medium text-foreground hover:text-primary">
                      {school.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">{[school.city, school.province].filter(Boolean).join(", ")}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <AccountBadge status={school.account_status} />
                      {school.archived && <Badge variant="secondary">Archived</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {school.awaiting.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {school.awaiting.map((awaiting) => <li key={awaiting.id}><AwaitingPayment awaiting={awaiting} /></li>)}
                      </ul>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {school.contact ? (
                      <>
                        <p className="text-foreground">{school.contact.name}</p>
                        <p className="text-xs text-muted-foreground">{school.contact.email}</p>
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">{school.campaign_count}</td>
                  <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">{school.signed_up_ago}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </AdminLayout>
  );
}
